import { formatExpirationDate } from "../utils/expiration";
import { useLocation, useNavigate } from "react-router-dom";
import { lazy, Suspense, useMemo, useEffect, useRef, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import { useApplication } from "../contexts/ApplicationContext";
import formatCurrency from "../utils/formatCurrency";
import { getActiveCompanyId } from "../utils/jwt";
import {
  PAYMENT_PHASE,
  daysUntilDue,
  getPaymentPhase,
} from "../utils/subscriptionPhase";
import BlockedCompanySwitch from "./common/BlockedCompanySwitch";
import TrialPlanBanner from "./common/TrialPlanBanner";

// Traz o swiper junto; o gate fica em todo o layout, mas o modal só aparece com
// plano expirado ou ao trocar de plano, então é baixado na primeira abertura.
const PlanSelectionModal = lazy(() => import("./modals/PlanSelectionModal"));

// A faixa do período de teste, depois de dispensada com o "x", não volta (por
// empresa, neste navegador).
const bannerKey = (companyId) => `relinq.trialBanner.dismissed.${companyId}`;
const readBannerDismissed = (companyId) => {
  try {
    return localStorage.getItem(bannerKey(companyId)) === "1";
  } catch (_) {
    return false;
  }
};
const writeBannerDismissed = (companyId) => {
  try {
    localStorage.setItem(bannerKey(companyId), "1");
  } catch (_) {}
};

// Fluxo de avisos de pagamento (RSL - 060): a escada BANNER → SOFT → DUE → HARD
// e o que cada degrau mostra estão em `utils/subscriptionPhase.js`. Aqui só se
// decide como cada um aparece:
//   BANNER  faixa laranja no topo (o "x" a dispensa); clicar abre os planos.
//   SOFT    modal ao abrir o app, com "Escolher plano depois".
//   DUE     modal ao abrir o app, sem "depois"; dá para ir pagar e sair.
//   HARD    modal fixo; sair da tela de pagamento sem pagar traz o modal de volta.
const SubscriptionGate = ({ children }) => {
  const { user, can } = useApplication();
  const location = useLocation();
  const navigate = useNavigate();

  const company = user?.Company || {};
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [isPlanModalOpen, setPlanModalOpen] = useState(false);
  const [planModalSelection, setPlanModalSelection] = useState({
    planId: null,
    cycleId: null,
  });
  const [selectedInterval, setSelectedInterval] = useState(12);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  // `null` = ainda não consultou o navegador; só mostra a faixa quando `false`.
  const [bannerDismissed, setBannerDismissed] = useState(null);
  // SOFT e DUE abrem sozinhos uma vez por abertura do app (carregamento da
  // página); HARD abre sempre que a pessoa estiver fora da tela de pagamento.
  const autoOpenedRef = useRef(false);
  // Depois da primeira abertura o modal permanece montado (mesmo comportamento
  // de antes ao fechar); antes disso nem o código dele é baixado.
  const [planModalMounted, setPlanModalMounted] = useState(false);
  useEffect(() => {
    if (isPlanModalOpen) setPlanModalMounted(true);
  }, [isPlanModalOpen]);

  const expirationValue =
    subscriptionData?.planExpirationDate ||
    company?.planExpirationDate ||
    company?.planExpiration ||
    null;
  const subscriptionStatus =
    subscriptionData?.planStatus ||
    company?.planStatus ||
    company?.subscriptionStatus ||
    "";
  const normalizedStatus = subscriptionStatus.toLowerCase();
  const isStatusActive = ["ativo", "active"].includes(normalizedStatus);
  const hasPlan = Boolean(company?.planId);
  const availablePlans = subscriptionData?.availablePlans || [];
  const canManageBilling = can("settings.billing.manage");
  const activeCompanyId = getActiveCompanyId(user);
  const phase = useMemo(
    () =>
      subscriptionLoaded
        ? getPaymentPhase({
            expiration: expirationValue,
            hasPlan,
            // A data de vencimento manda em tudo: `planStatus` chega do gateway
            // com valores como "RECEIVED"/"CONFIRMED", que não passam em
            // `isStatusActive`, e o status sozinho derrubava quem tinha o plano
            // pago e em dia. O status só decide quando não há data para contar.
            blocked: !isStatusActive,
          })
        : PAYMENT_PHASE.NONE,
    [expirationValue, hasPlan, isStatusActive, subscriptionLoaded],
  );
  const isForcedPhase =
    phase === PAYMENT_PHASE.DUE || phase === PAYMENT_PHASE.HARD;
  const dueInDays = daysUntilDue(expirationValue);

  useEffect(() => {
    let active = true;
    const fetchSubscription = async () => {
      try {
        const { data } = await axiosInstance.get("/assinatura");
        if (!active) return;
        setSubscriptionData(data?.data ?? null);
      } catch (error) {
        console.error("Erro ao carregar assinatura", error);
      } finally {
        if (active) {
          setSubscriptionLoaded(true);
        }
      }
    };
    fetchSubscription();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!availablePlans.length) return;
    if (planModalSelection.planId) return;
    const fallbackPlan = availablePlans[0];
    const fallbackCycle = fallbackPlan?.cycles?.[0];
    setPlanModalSelection({
      planId: fallbackPlan?.id ?? null,
      cycleId: fallbackCycle?.id ?? null,
    });
  }, [availablePlans, planModalSelection.planId]);

  const findCycleForPlan = (plan, interval) =>
    (plan?.cycles || []).find((cycle) => cycle.intervalMonths === interval);

  const availableIntervals = useMemo(() => {
    const monthsSet = new Set();
    availablePlans.forEach((plan) => {
      (plan.cycles || []).forEach((cycle) => {
        const monthValue = Number(cycle.intervalMonths);
        if (Number.isFinite(monthValue)) {
          monthsSet.add(monthValue);
        }
      });
    });
    return Array.from(monthsSet).sort((a, b) => a - b);
  }, [availablePlans]);

  useEffect(() => {
    if (availableIntervals.length === 0) return;
    if (availableIntervals.includes(selectedInterval)) return;
    const preferred = availableIntervals.includes(12)
      ? 12
      : availableIntervals[availableIntervals.length - 1];
    setSelectedInterval(preferred);
  }, [availableIntervals, selectedInterval]);

  useEffect(() => {
    const plan = availablePlans.find((p) => p.id === planModalSelection.planId);
    if (!plan) return;
    const cycle = findCycleForPlan(plan, selectedInterval);
    if (!cycle) return;
    setPlanModalSelection((prev) => ({
      ...prev,
      cycleId: cycle.id,
    }));
  }, [selectedInterval, availablePlans, planModalSelection.planId]);

  const isPaymentRoute = location.pathname.startsWith("/assinatura/pagamento");

  // A faixa dispensada não volta: consulta o navegador quando a empresa ativa
  // é conhecida (e de novo se ela mudar).
  useEffect(() => {
    if (!activeCompanyId) return;
    setBannerDismissed(readBannerDismissed(activeCompanyId));
  }, [activeCompanyId]);

  useEffect(() => {
    if (!subscriptionLoaded || !canManageBilling || isPaymentRoute) return;
    if (phase === PAYMENT_PHASE.HARD) {
      // Sem saída: o modal está sempre lá fora da tela de pagamento (inclusive
      // ao voltar dela sem ter pago).
      setPlanModalOpen(true);
      return;
    }
    if (
      (phase === PAYMENT_PHASE.SOFT || phase === PAYMENT_PHASE.DUE) &&
      !autoOpenedRef.current
    ) {
      autoOpenedRef.current = true;
      setPlanModalOpen(true);
    }
  }, [canManageBilling, isPaymentRoute, phase, subscriptionLoaded]);

  if (isPaymentRoute) {
    return children;
  }

  const expirationLabel = formatExpirationDate(expirationValue);

  // Papel sem `settings.billing.manage` (a mesma chave que o servidor exige no
  // pagamento) não escolhe plano: só o bloqueio total (HARD) o barra, com um
  // aviso para falar com quem administra; nos degraus anteriores nada o
  // interrompe. O botão de trocar de empresa segue.
  if (!canManageBilling) {
    if (phase !== PAYMENT_PHASE.HARD) return children;
    const knownExpiration = formatExpirationDate(expirationValue, "");
    return (
      <>
        {children}

        <BlockedCompanySwitch />

        <div className="dark-backdrop fixed inset-0 z-[1100] flex items-center justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="dark-panel w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900">
              Assinatura vencida
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {knownExpiration
                ? `O plano desta empresa venceu em ${knownExpiration}.`
                : "O plano desta empresa está vencido."}{" "}
              Peça a um administrador para renovar a assinatura.
            </p>
          </div>
        </div>
      </>
    );
  }

  const handleChoosePlan = (
    confirmedPlan = null,
    confirmedCycle = null,
    confirmedPlanId = null,
    confirmedCycleId = null,
    confirmedCycleInterval = null
  ) => {
    const planIdToUse =
      confirmedPlanId ?? planModalSelection.planId ?? confirmedPlan?.id;
    const cycleIdToUse =
      confirmedCycleId ?? planModalSelection.cycleId ?? confirmedCycle?.id;
    const plan =
      confirmedPlan ||
      availablePlans.find((p) => p.id === planIdToUse) ||
      availablePlans[0];
    const cycle =
      confirmedCycle ||
      (plan ? (plan.cycles || []).find((c) => c.id === cycleIdToUse) : null);
    if (!plan || !cycle) return;
    const targetPath = `/assinatura/pagamento`;
    const params = new URLSearchParams({
      planId: String(planIdToUse ?? plan.id),
      cycleId: String(cycleIdToUse ?? cycle.id),
    }).toString();
    setPlanModalOpen(false);
    navigate(`${targetPath}?${params}`, {
      state: {
        planName: plan.name,
        planDescription: plan.description,
        cycleIntervalMonths: confirmedCycleInterval ?? cycle.intervalMonths,
      },
    });
  };

  const handleCloseModal = () => {
    if (isForcedPhase) return;
    setPlanModalOpen(false);
  };

  const handleDismissBanner = () => {
    writeBannerDismissed(activeCompanyId);
    setBannerDismissed(true);
  };

  // Textos do modal conforme o degrau do fluxo.
  const modalCopy =
    phase === PAYMENT_PHASE.HARD
      ? {
          title: "Seu plano venceu",
          subtitle:
            "O acesso está bloqueado. Escolha um plano para continuar usando o sistema.",
        }
      : phase === PAYMENT_PHASE.DUE
        ? {
            title: "Seu plano venceu",
            subtitle: "Para evitar bloqueios na conta, escolha um plano.",
          }
        : phase === PAYMENT_PHASE.SOFT
          ? {
              title: "Seu período de teste está acabando",
              subtitle: `Seu teste termina em ${expirationLabel}. Escolha um plano para continuar usando o sistema sem interrupções.`,
            }
          : {};

  return (
    <>
      {phase === PAYMENT_PHASE.BANNER && bannerDismissed === false ? (
        <TrialPlanBanner
          daysLeft={dueInDays}
          onChoose={() => setPlanModalOpen(true)}
          onDismiss={handleDismissBanner}
        />
      ) : null}

      {children}

      {/* Saída do bloqueio de plano: só enquanto o modal sem saída está aberto. */}
      {isForcedPhase && isPlanModalOpen ? <BlockedCompanySwitch /> : null}

      {planModalMounted ? (
        <Suspense fallback={null}>
          <PlanSelectionModal
            isOpen={isPlanModalOpen}
            onClose={handleCloseModal}
            disableBackdropClick={isForcedPhase}
            title={modalCopy.title}
            subtitle={modalCopy.subtitle}
            secondaryActionLabel={
              phase === PAYMENT_PHASE.SOFT ? "Escolher plano depois" : null
            }
            onSecondaryAction={handleCloseModal}
            availablePlans={availablePlans}
            planModalSelection={planModalSelection}
            setPlanModalSelection={setPlanModalSelection}
            selectedInterval={selectedInterval}
            setSelectedInterval={setSelectedInterval}
            expandedPlanId={expandedPlanId}
            setExpandedPlanId={setExpandedPlanId}
            onConfirm={handleChoosePlan}
            formatCurrency={formatCurrency}
            confirmDisabled={
              !planModalSelection.planId || !planModalSelection.cycleId
            }
          />
        </Suspense>
      ) : null}
    </>
  );
};

export default SubscriptionGate;
